import { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "./AuthContext";
import {
  Mail, Inbox, Send, Folder, Plus, X, Check, RefreshCw, Trash2,
  ChevronLeft, AlertTriangle, Circle, PenSquare, Settings,
  Star, Clock, Archive, File, Search, Paperclip, Download,
  Eye, FileText, Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import BackButton from "./BackButton";
import { sanitizeHtml, htmlToPlainText } from "../utils/sanitize";

const AUTO_REFRESH_MS = 3 * 60 * 1000; // 3 minutes
const CACHE_KEY = "gow_webmail_cache_v1";

const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch (e) { console.warn("[webmail] failed to parse cache:", e); return {}; }
};
const saveCache = (cache) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); }
  catch (e) { console.warn("[webmail] failed to persist cache (quota?):", e); }
};
const cacheKeyFor = (accId, folder) => `${accId}::${folder}`;

const FOLDER_ICONS = {
  INBOX: Inbox, Sent: Send, "Sent Mail": Send, "Sent Items": Send, "[Gmail]/Sent Mail": Send,
  Drafts: File, "[Gmail]/Drafts": File, Trash: Trash2, "[Gmail]/Trash": Trash2,
  Spam: AlertTriangle, "[Gmail]/Spam": AlertTriangle, Junk: AlertTriangle,
  Starred: Star, "[Gmail]/Starred": Star, Archive: Archive, "[Gmail]/All Mail": Archive,
};
const FOLDER_LABELS = {
  INBOX: "Inbox", Sent: "Sent", "Sent Mail": "Sent", "Sent Items": "Sent", "[Gmail]/Sent Mail": "Sent",
  Drafts: "Drafts", "[Gmail]/Drafts": "Drafts", Trash: "Trash", "[Gmail]/Trash": "Trash",
  Spam: "Spam", "[Gmail]/Spam": "Spam", Junk: "Spam", "[Gmail]/Starred": "Starred", "[Gmail]/All Mail": "All Mail",
};
const cleanFolderName = (r) => FOLDER_LABELS[r] || r.replace(/^\[Gmail\]\/?/i, "").replace(/^INBOX\./i, "") || r;
const getFolderIcon = (r) => FOLDER_ICONS[r] || Folder;
const formatSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

export default function WebmailModule({ prefillCompose = null, onPrefillConsumed = () => {} }) {
  const api = useApi();
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [folders, setFolders] = useState(["INBOX"]);
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [view, setView] = useState("inbox");
  const [connectionStatus, setConnectionStatus] = useState({});
  const [authError, setAuthError] = useState(null);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "" });
  const [composeFiles, setComposeFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ login: "", password: "", imap_host: "imap.gmail.com", imap_port: 993, smtp_host: "smtp.gmail.com", smtp_port: 587, label: "" });
  const [testing, setTesting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUnread, setFilterUnread] = useState(false);
  const [attachPreview, setAttachPreview] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [newMailCount, setNewMailCount] = useState(0);
  // Outbox state
  const [outboxItems, setOutboxItems] = useState([]);
  const [outboxCounts, setOutboxCounts] = useState({ sending: 0, failed: 0, pending: 0 });
  const refreshTimer = useRef(null);
  const outboxTimer = useRef(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email/accounts");
      setAccounts(data);
      if (data.length > 0 && !activeAccount) setActiveAccount(data[0]);
    } catch (err) { console.warn("[webmail] fetchAccounts failed:", err?.message || err); }
  }, [api, activeAccount]);

  // Outbox: lightweight count poll (used to decorate the sidebar badge) + full list (used in Outbox view)
  const fetchOutboxCount = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email/outbox/count");
      setOutboxCounts(data);
    } catch (err) { console.warn("[webmail] fetchOutboxCount failed:", err?.message || err); }
  }, [api]);

  const fetchOutboxItems = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email/outbox");
      setOutboxItems(data);
    } catch (err) { console.warn("[webmail] fetchOutboxItems failed:", err?.message || err); }
  }, [api]);

  const handleOutboxRetry = async (id) => {
    try {
      await api.post(`/admin/email/outbox/${id}/retry`);
      toast.success("Retrying send…");
      fetchOutboxItems();
      fetchOutboxCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    }
  };

  const handleOutboxDelete = async (id) => {
    if (!window.confirm("Discard this outbox entry?")) return;
    try {
      await api.delete(`/admin/email/outbox/${id}`);
      toast.success("Removed");
      fetchOutboxItems();
      fetchOutboxCount();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Poll outbox count every 5s so badge updates as background sends complete.
  // Refresh the items list more aggressively when user is viewing the Outbox folder.
  useEffect(() => {
    fetchOutboxCount();
    const interval = setInterval(fetchOutboxCount, 5000);
    return () => clearInterval(interval);
  }, [fetchOutboxCount]);

  useEffect(() => {
    if (activeFolder === "OUTBOX") {
      fetchOutboxItems();
      outboxTimer.current = setInterval(fetchOutboxItems, 3000);
      return () => clearInterval(outboxTimer.current);
    }
  }, [activeFolder, fetchOutboxItems]);

  // Consume external compose prefill (from Clients/Budgets quick-email shortcut)
  useEffect(() => {
    if (!prefillCompose) return;
    if (accounts.length === 0) return; // wait for accounts to load
    setCompose({
      to: prefillCompose.to || "",
      subject: prefillCompose.subject || "",
      body: prefillCompose.body || "",
    });
    setComposeFiles([]);
    setView("compose");
    onPrefillConsumed();
    toast.success("Compose opened with prefilled details");
  }, [prefillCompose, accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFolders = useCallback(async () => {
    if (!activeAccount) return;
    try {
      const { data } = await api.get(`/admin/email/folders?account_id=${activeAccount.id}`);
      setFolders(data);
      setConnectionStatus(p => ({ ...p, [activeAccount.id]: true }));
    } catch (err) {
      setFolders(["INBOX"]);
      setConnectionStatus(p => ({ ...p, [activeAccount.id]: false }));
      if (err.response?.status === 502) setAuthError(activeAccount.login);
    }
  }, [api, activeAccount]);

  const fetchMessages = useCallback(async ({ silent = false, isAutoRefresh = false } = {}) => {
    if (!activeAccount) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/admin/email/messages?account_id=${activeAccount.id}&folder=${encodeURIComponent(activeFolder)}&limit=50`);
      // Detect new messages compared to current state
      if (isAutoRefresh) {
        const seenUids = new Set(messages.map(m => m.uid));
        const fresh = data.filter(m => !seenUids.has(m.uid)).length;
        if (fresh > 0) {
          setNewMailCount(c => c + fresh);
          toast.success(`${fresh} new email${fresh > 1 ? "s" : ""} arrived`);
        }
      }
      setMessages(data);
      setLastSync(new Date());
      // Persist to localStorage cache (last 50 message previews per folder)
      const cache = loadCache();
      cache[cacheKeyFor(activeAccount.id, activeFolder)] = { messages: data, ts: Date.now() };
      saveCache(cache);
      setConnectionStatus(p => ({ ...p, [activeAccount.id]: true }));
    } catch (err) {
      if (!silent) setMessages([]);
      setConnectionStatus(p => ({ ...p, [activeAccount.id]: false }));
      if (err.response?.status === 502) setAuthError(activeAccount.login);
    } finally { if (!silent) setLoading(false); }
  }, [api, activeAccount, activeFolder, messages]);

  // Hydrate from cache instantly when account/folder switches.
  useEffect(() => {
    if (!activeAccount) return;
    const cache = loadCache();
    const entry = cache[cacheKeyFor(activeAccount.id, activeFolder)];
    if (entry?.messages?.length) {
      setMessages(entry.messages);
      setLastSync(entry.ts ? new Date(entry.ts) : null);
    }
  }, [activeAccount, activeFolder]);

  useEffect(() => {
    if (activeAccount) { fetchFolders(); fetchMessages(); }
  }, [activeAccount, activeFolder, fetchFolders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh inbox every AUTO_REFRESH_MS while module is open.
  useEffect(() => {
    if (!activeAccount) return;
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    refreshTimer.current = setInterval(() => {
      fetchMessages({ silent: true, isAutoRefresh: true });
    }, AUTO_REFRESH_MS);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [activeAccount, activeFolder, fetchMessages]);

  const handleReadMsg = async (msg) => {
    if (!activeAccount) return;
    try {
      const { data } = await api.get(`/admin/email/message/${msg.uid}?account_id=${activeAccount.id}&folder=${encodeURIComponent(activeFolder)}`);
      setSelectedMsg(data);
      setView("read");
      // Mark locally as read
      setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, is_read: true } : m));
    } catch (err) { toast.error("Failed to load message"); }
  };

  const handleDownloadAttachment = async (uid, filename) => {
    try {
      const resp = await api.get(`/admin/email/attachment/${uid}?account_id=${activeAccount.id}&filename=${encodeURIComponent(filename)}&folder=${encodeURIComponent(activeFolder)}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const ct = resp.headers["content-type"] || "";
      if (ct.startsWith("image/") || ct === "application/pdf") {
        setAttachPreview({ url, filename, type: ct });
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { toast.error("Failed to download attachment"); }
  };

  const htmlToText = (html) => htmlToPlainText(html);

  const handleSend = async () => {
    if (!compose.to || !compose.subject) { toast.error("To and Subject required"); return; }
    setSending(true);
    try {
      const bodyHtml = compose.body || "";
      const bodyText = htmlToText(bodyHtml);
      if (composeFiles.length > 0) {
        const fd = new FormData();
        fd.append("account_id", activeAccount.id);
        fd.append("to", compose.to);
        fd.append("subject", compose.subject);
        fd.append("body_text", bodyText);
        fd.append("body_html", bodyHtml);
        composeFiles.forEach(f => fd.append("files", f));
        await api.post("/admin/email/send-with-attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/admin/email/send", { account_id: activeAccount.id, to: compose.to, subject: compose.subject, body_text: bodyText, body_html: bodyHtml });
      }
      toast.success("Email queued — sending in background…");
      setCompose({ to: "", subject: "", body: "" });
      setComposeFiles([]);
      // Navigate to the Outbox so the user sees the live status (sending → sent or failed)
      setActiveFolder("OUTBOX");
      setView("inbox");
      fetchOutboxItems();
      fetchOutboxCount();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/email/accounts", accountForm);
      toast.success("Email account added");
      setShowAddAccount(false);
      setAccountForm({ login: "", password: "", imap_host: "imap.gmail.com", imap_port: 993, smtp_host: "smtp.gmail.com", smtp_port: 587, label: "" });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleTestConnection = async () => {
    if (!activeAccount) return;
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/email/accounts/${activeAccount.id}/test`);
      if (data.imap.ok && data.smtp.ok) {
        toast.success("Connection OK!"); setConnectionStatus(p => ({ ...p, [activeAccount.id]: true }));
      } else {
        toast.error([!data.imap.ok && data.imap.message, !data.smtp.ok && data.smtp.message].filter(Boolean).join(" | "));
        setConnectionStatus(p => ({ ...p, [activeAccount.id]: false }));
      }
    } catch (err) { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  const handleDeleteAccount = async (accId) => {
    try { await api.delete(`/admin/email/accounts/${accId}`); toast.success("Removed"); if (activeAccount?.id === accId) setActiveAccount(null); fetchAccounts(); }
    catch (err) { toast.error("Failed"); }
  };

  const formatTime = (ds) => {
    if (!ds) return "";
    try {
      const d = new Date(ds); const n = new Date();
      if (d.toDateString() === n.toDateString()) return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
      if (d.getFullYear() === n.getFullYear()) return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch (err) { return ds.slice(0, 16); }
  };
  const extractName = (f) => { if (!f) return "?"; const m = f.match(/^"?(.+?)"?\s*</) || f.match(/^(.+?)@/); return m ? m[1].trim() : f.split("@")[0]; };

  const filteredMessages = messages.filter(m => {
    if (filterUnread && m.is_read) return false;
    if (searchQuery && !(m.subject + m.from + m.snippet).toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const unreadCount = messages.filter(m => !m.is_read).length;

  // ===== Empty state =====
  if (accounts.length === 0 && !showAddAccount) {
    return (
      <div data-testid="webmail-module" className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-forest-600" /></div>
          <div><h2 className="text-lg font-semibold text-stone-800">Webmail</h2><p className="text-sm text-stone-500">Configure email accounts to get started</p></div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <Mail className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-600 mb-2">No email accounts configured</h3>
          <p className="text-sm text-stone-400 mb-6">Add your email to start receiving and sending messages.</p>
          <button onClick={() => setShowAddAccount(true)} data-testid="add-email-btn" className="flex items-center gap-2 mx-auto bg-forest-600 hover:bg-forest-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Add Email Account
          </button>
        </div>
        {renderModal()}
      </div>
    );
  }

  function renderModal() {
    if (!showAddAccount) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="add-account-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-800 flex items-center gap-2"><Mail className="w-5 h-5 text-forest-600" /> Add Email Account</h3>
            <button onClick={() => setShowAddAccount(false)} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleAddAccount} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs font-medium text-stone-500 mb-1 block">Email / Login *</label>
                <input data-testid="email-login" value={accountForm.login} onChange={e => setAccountForm(f => ({ ...f, login: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required placeholder="user@company.com" /></div>
              <div className="col-span-2"><label className="text-xs font-medium text-stone-500 mb-1 block">Password / App Password *</label>
                <input data-testid="email-password" type="password" value={accountForm.password} onChange={e => setAccountForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required /></div>
              <div><label className="text-xs font-medium text-stone-500 mb-1 block">IMAP Server</label>
                <input value={accountForm.imap_host} onChange={e => setAccountForm(f => ({ ...f, imap_host: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-stone-500 mb-1 block">IMAP Port</label>
                <input type="number" value={accountForm.imap_port} onChange={e => setAccountForm(f => ({ ...f, imap_port: Number(e.target.value) }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-stone-500 mb-1 block">SMTP Server</label>
                <input value={accountForm.smtp_host} onChange={e => setAccountForm(f => ({ ...f, smtp_host: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-stone-500 mb-1 block">SMTP Port</label>
                <input type="number" value={accountForm.smtp_port} onChange={e => setAccountForm(f => ({ ...f, smtp_port: Number(e.target.value) }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
              <div className="col-span-2"><label className="text-xs font-medium text-stone-500 mb-1 block">Label</label>
                <input value={accountForm.label} onChange={e => setAccountForm(f => ({ ...f, label: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. Contact Us" /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="save-email-account-btn" className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium"><Check className="w-4 h-4" /> Save</button>
              <button type="button" onClick={() => setShowAddAccount(false)} className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const isConn = connectionStatus[activeAccount?.id];

  return (
    <div data-testid="webmail-module" className="flex h-[calc(100vh-120px)] bg-stone-50 -m-6 -mt-4">
      {/* ===== Sidebar ===== */}
      <div className="w-56 bg-white flex flex-col border-r border-stone-200 flex-shrink-0">
        <div className="p-3 pb-2">
          <select data-testid="account-selector" value={activeAccount?.id || ""} onChange={e => { setActiveAccount(accounts.find(a => a.id === e.target.value)); setMessages([]); setActiveFolder("INBOX"); setView("inbox"); }}
            className="w-full px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white text-stone-700 truncate">
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label || a.login}</option>)}
          </select>
          <div className="flex items-center gap-1.5 mt-1.5 px-1">
            <Circle className={`w-2 h-2 flex-shrink-0 ${isConn ? "fill-green-500 text-green-500" : "fill-red-400 text-red-400"}`} data-testid="connection-status" />
            <span className={`text-[10px] ${isConn ? "text-green-600" : "text-red-500"}`}>{isConn ? "Connected" : "Disconnected"}</span>
            <button onClick={handleTestConnection} disabled={testing} title="Test" className="ml-auto p-0.5 rounded text-stone-400 hover:text-forest-600">
              <RefreshCw className={`w-3 h-3 ${testing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        <div className="px-3 pb-3">
          <button onClick={() => { setCompose({ to: "", subject: "", body: "" }); setComposeFiles([]); setView("compose"); }} data-testid="compose-btn"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-forest-50 border border-forest-200 text-forest-700 text-sm font-medium hover:bg-forest-100 hover:shadow-sm transition-all">
            <PenSquare className="w-4 h-4" /> Compose
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 space-y-0.5">
          {/* Outbox virtual folder — always above provider folders so it's the most visible */}
          {(() => {
            const isActive = activeFolder === "OUTBOX" && view === "inbox";
            return (
              <button
                data-testid="folder-OUTBOX"
                onClick={() => { setActiveFolder("OUTBOX"); setView("inbox"); setSelectedMsg(null); fetchOutboxItems(); }}
                className={`w-full text-left px-3 py-2 rounded-full text-[13px] flex items-center gap-3 transition-colors ${
                  isActive ? "bg-amber-100 text-amber-900 font-semibold" : "text-stone-700 hover:bg-stone-100"
                }`}
              >
                <Send className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-amber-700" : "text-stone-500"}`} />
                <span className="truncate flex-1">Outbox</span>
                {outboxCounts.sending > 0 && (
                  <span data-testid="outbox-badge-sending" className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                    {outboxCounts.sending}
                  </span>
                )}
                {outboxCounts.failed > 0 && (
                  <span data-testid="outbox-badge-failed" className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {outboxCounts.failed}
                  </span>
                )}
              </button>
            );
          })()}
          {folders.map(f => {
            const Icon = getFolderIcon(f);
            const label = cleanFolderName(f);
            const isActive = activeFolder === f && view === "inbox";
            return (
              <button key={f} onClick={() => { setActiveFolder(f); setView("inbox"); setSelectedMsg(null); }}
                className={`w-full text-left px-3 py-2 rounded-full text-[13px] flex items-center gap-3 transition-colors ${isActive ? "bg-forest-100 text-forest-800 font-semibold" : "text-stone-700 hover:bg-stone-100"}`}>
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-forest-700" : "text-stone-500"}`} />
                <span className="truncate flex-1">{label}</span>
                {f === "INBOX" && unreadCount > 0 && <span className="bg-forest-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-2 border-t border-stone-100 space-y-0.5">
          <button onClick={() => setShowAddAccount(true)} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-stone-500 hover:text-forest-600 hover:bg-stone-50 rounded-full"><Plus className="w-3.5 h-3.5" /> Add Account</button>
          <button onClick={() => setView("settings")} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-stone-500 hover:text-forest-600 hover:bg-stone-50 rounded-full"><Settings className="w-3.5 h-3.5" /> Settings</button>
        </div>
      </div>

      {/* ===== Main ===== */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">

        {/* INBOX */}
        {view === "inbox" && activeFolder === "OUTBOX" && (
          <div className="flex flex-col h-full" data-testid="outbox-view">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-3">
              <Send className="w-4 h-4 text-amber-600" />
              <h2 className="font-semibold text-stone-800 text-sm">Outbox</h2>
              <span className="text-xs text-stone-500">
                {outboxCounts.sending > 0 && `${outboxCounts.sending} sending · `}
                {outboxCounts.failed > 0 && `${outboxCounts.failed} failed · `}
                {outboxItems.length} total
              </span>
              <button onClick={fetchOutboxItems} title="Refresh" className="ml-auto p-2 rounded-full hover:bg-stone-100 text-stone-500">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {outboxItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                  <Send className="w-16 h-16 mb-3 opacity-20" />
                  <p className="text-sm font-medium">Outbox is empty</p>
                  <p className="text-xs mt-1">Sent emails appear here briefly while delivering</p>
                </div>
              ) : outboxItems.map((item) => {
                const statusBadge = {
                  sending: { bg: "bg-amber-100", text: "text-amber-700", label: "Sending…", pulse: true },
                  sent: { bg: "bg-green-100", text: "text-green-700", label: "Sent", pulse: false },
                  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed", pulse: false },
                }[item.status] || { bg: "bg-stone-100", text: "text-stone-700", label: item.status, pulse: false };
                return (
                  <div
                    key={item.id}
                    data-testid={`outbox-row-${item.id}`}
                    className={`px-4 py-3 border-b border-stone-100 ${
                      item.status === "failed" ? "bg-red-50/30" :
                      item.status === "sending" ? "bg-amber-50/30" :
                      "hover:bg-stone-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusBadge.bg} ${statusBadge.text} ${statusBadge.pulse ? "animate-pulse" : ""}`}>
                            {statusBadge.label}
                          </span>
                          <span className="text-sm font-semibold text-stone-800 truncate">{item.to}</span>
                          {item.imap_append_ok === false && item.status === "sent" && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-200" title="SMTP delivered, but couldn't save copy to Sent folder">
                              ! Sent folder skipped
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-700 mt-0.5 truncate">{item.subject}</p>
                        {item.error && (
                          <p className="text-xs text-red-600 mt-1 break-words" title={item.error}>
                            <span className="font-semibold">Error:</span> {item.error.substring(0, 220)}
                          </p>
                        )}
                        <p className="text-[11px] text-stone-400 mt-1">
                          {item.sent_at
                            ? `Sent at ${new Date(item.sent_at).toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, month: "short", day: "numeric" })}`
                            : `Queued at ${new Date(item.created_at).toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, month: "short", day: "numeric" })}`
                          }
                          {item.attachments_count > 0 && ` · ${item.attachments_count} attachment(s)`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {item.status === "failed" && (
                          <button
                            onClick={() => handleOutboxRetry(item.id)}
                            data-testid={`outbox-retry-${item.id}`}
                            className="px-2.5 py-1 rounded-md bg-forest-600 text-white text-xs font-medium hover:bg-forest-700"
                          >
                            Retry
                          </button>
                        )}
                        {item.status !== "sending" && (
                          <button
                            onClick={() => handleOutboxDelete(item.id)}
                            data-testid={`outbox-delete-${item.id}`}
                            className="p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50"
                            title="Discard"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {view === "inbox" && activeFolder !== "OUTBOX" && (<>
          <div className="px-4 py-2 border-b border-stone-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search mail" data-testid="mail-search"
                className="w-full pl-10 pr-4 py-2 bg-stone-100 rounded-lg text-sm border-0 focus:bg-white focus:ring-2 focus:ring-forest-500 transition-colors" />
            </div>
            <button onClick={() => setFilterUnread(!filterUnread)} data-testid="filter-unread-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterUnread ? "bg-forest-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              <Mail className="w-3.5 h-3.5" /> Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
            </button>
            <button onClick={() => fetchMessages()} title="Refresh" className="p-2 rounded-full hover:bg-stone-100 text-stone-500">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <div className="flex items-center gap-2 text-xs text-stone-400">
              <span>{filteredMessages.length} of {messages.length}</span>
              {lastSync && <span className="hidden md:inline" data-testid="last-sync">· Last sync {lastSync.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>}
              {newMailCount > 0 && (
                <button onClick={() => setNewMailCount(0)} data-testid="new-mail-pill" className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-forest-100 text-forest-700 hover:bg-forest-200">
                  {newMailCount} new
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && messages.length === 0 ? (
              <div className="flex items-center justify-center h-40"><RefreshCw className="w-5 h-5 text-stone-400 animate-spin" /></div>
            ) : filteredMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                <Inbox className="w-16 h-16 mb-3 opacity-20" />
                <p className="text-sm font-medium">{filterUnread ? "No unread messages" : `No messages in ${cleanFolderName(activeFolder)}`}</p>
              </div>
            ) : filteredMessages.map((msg, idx) => (
              <div key={msg.uid || idx} onClick={() => handleReadMsg(msg)} data-testid={`msg-row-${idx}`}
                className={`flex items-center gap-3 px-4 py-2.5 border-b border-stone-100 cursor-pointer transition-colors group ${msg.is_read ? "hover:bg-stone-50" : "bg-blue-50/40 hover:bg-blue-50/70"}`}>
                {/* Unread dot */}
                <div className="w-2.5 flex-shrink-0">
                  {!msg.is_read && <Circle className="w-2.5 h-2.5 fill-forest-500 text-forest-500" data-testid={`unread-dot-${idx}`} />}
                </div>
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs ${msg.is_read ? "bg-stone-200 text-stone-600" : "bg-forest-100 text-forest-700 font-bold"}`}>
                  {extractName(msg.from).charAt(0).toUpperCase()}
                </div>
                {/* From */}
                <div className="w-40 flex-shrink-0">
                  <p className={`text-sm truncate ${msg.is_read ? "text-stone-600" : "font-bold text-stone-900"}`}>{extractName(msg.from)}</p>
                </div>
                {/* Subject + snippet */}
                <div className="flex-1 min-w-0 flex items-center gap-1">
                  <span className={`text-sm truncate ${msg.is_read ? "text-stone-600" : "font-semibold text-stone-800"}`}>{msg.subject || "(no subject)"}</span>
                  <span className="text-sm text-stone-400 truncate hidden lg:inline"> - {msg.snippet}</span>
                </div>
                {/* Attachment icon */}
                {msg.has_attachments && <Paperclip className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />}
                {/* Time */}
                <span className={`text-xs flex-shrink-0 ${msg.is_read ? "text-stone-400" : "font-semibold text-stone-700"}`}>{formatTime(msg.date)}</span>
              </div>
            ))}
          </div>
        </>)}

        {/* READ */}
        {view === "read" && selectedMsg && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-3 sticky top-0 bg-white z-10">
              <button onClick={() => { setView("inbox"); setSelectedMsg(null); }} className="p-2 rounded-full hover:bg-stone-100 text-stone-500"><ChevronLeft className="w-5 h-5" /></button>
              <h3 className="text-lg font-medium text-stone-800 truncate flex-1">{selectedMsg.subject}</h3>
            </div>
            <div className="px-5 pt-2">
              <BackButton onClick={() => { setView("inbox"); setSelectedMsg(null); }} testId="read-back" label="Back to Inbox" />
            </div>
            <div className="max-w-4xl mx-auto p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-forest-100 flex items-center justify-center flex-shrink-0 text-forest-700 font-bold text-sm">
                  {extractName(selectedMsg.from).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-stone-800">{extractName(selectedMsg.from)}</p>
                    <span className="text-xs text-stone-400">&lt;{selectedMsg.from.match(/<(.+?)>/)?.[1] || selectedMsg.from}&gt;</span>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">to {selectedMsg.to}</p>
                  <p className="text-xs text-stone-400">{selectedMsg.date}</p>
                </div>
              </div>
              {/* Attachments */}
              {selectedMsg.attachments?.length > 0 && (
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200" data-testid="msg-attachments">
                  <p className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> {selectedMsg.attachments.length} Attachment{selectedMsg.attachments.length > 1 ? "s" : ""}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMsg.attachments.map((att, i) => (
                      <button key={`${att.filename}-${i}`} onClick={() => handleDownloadAttachment(selectedMsg.uid, att.filename)} data-testid={`download-att-${i}`}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 hover:border-forest-300 transition-colors text-sm">
                        {att.content_type?.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-stone-500" />}
                        <span className="text-stone-700 truncate max-w-[150px]">{att.filename}</span>
                        <span className="text-xs text-stone-400">{formatSize(att.size)}</span>
                        <Download className="w-3.5 h-3.5 text-stone-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Body */}
              {selectedMsg.body_html ? (
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedMsg.body_html) }} />
              ) : (
                <pre className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{selectedMsg.body_text}</pre>
              )}
            </div>
          </div>
        )}

        {/* COMPOSE */}
        {view === "compose" && (
          <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden" data-testid="compose-pane">
            {/* Header bar (shrinks) */}
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-3 bg-stone-800 text-white flex-shrink-0">
              <h3 className="text-sm font-medium flex-1">New Message</h3>
              <button onClick={() => setView("inbox")} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 pt-2 flex-shrink-0">
              <BackButton onClick={() => setView("inbox")} testId="compose-back" label="Back to Inbox" />
            </div>
            {/* To */}
            <div className="border-b border-stone-200 px-5 py-2 flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-stone-500 w-16">To</span>
              <input data-testid="compose-to" value={compose.to} onChange={e => setCompose(c => ({ ...c, to: e.target.value }))} className="flex-1 text-sm border-0 outline-none py-1" placeholder="Recipients" />
            </div>
            {/* Subject */}
            <div className="border-b border-stone-200 px-5 py-2 flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-stone-500 w-16">Subject</span>
              <input data-testid="compose-subject" value={compose.subject} onChange={e => setCompose(c => ({ ...c, subject: e.target.value }))} className="flex-1 text-sm border-0 outline-none py-1" />
            </div>
            {/* Editor — only this region scrolls internally */}
            <RichTextEditor
              value={compose.body}
              onChange={(html) => setCompose(c => ({ ...c, body: html }))}
              placeholder="Compose email..."
              testId="compose-body"
            />
            {/* Attached files (shrinks) */}
            {composeFiles.length > 0 && (
              <div className="px-5 py-2 border-t border-stone-100 flex flex-wrap gap-2 flex-shrink-0 bg-white">
                {composeFiles.map((f, i) => (
                  <span key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-1.5 bg-stone-100 rounded-lg px-2.5 py-1 text-xs text-stone-600">
                    <Paperclip className="w-3 h-3" />{f.name} ({formatSize(f.size)})
                    <button onClick={() => setComposeFiles(cf => cf.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            {/* Sticky footer with Send (always visible) */}
            <div className="px-5 py-3 border-t border-stone-200 flex items-center gap-3 bg-stone-50 flex-shrink-0" data-testid="compose-footer">
              <button onClick={handleSend} disabled={sending} data-testid="send-email-btn"
                className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-stone-400 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? "Sending..." : "Send"}
              </button>
              <label className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-200 cursor-pointer transition-colors" data-testid="attach-btn">
                <Paperclip className="w-4 h-4" /> Attach
                <input type="file" multiple className="hidden" onChange={e => setComposeFiles(prev => [...prev, ...Array.from(e.target.files)])} />
              </label>
              <button onClick={() => setView("inbox")} className="text-sm text-stone-500 hover:text-stone-700 px-3 py-2">Discard</button>
              <span className="text-xs text-stone-400 ml-auto truncate">from {activeAccount?.login}</span>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {view === "settings" && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-3">
              <button onClick={() => setView("inbox")} className="p-2 rounded-full hover:bg-stone-100 text-stone-500"><ChevronLeft className="w-5 h-5" /></button>
              <h3 className="text-sm font-semibold text-stone-800">Email Accounts</h3>
            </div>
            <div className="max-w-2xl mx-auto p-6 space-y-3">
              {accounts.map(acc => (
                <div key={acc.id} className="flex items-center justify-between bg-stone-50 rounded-xl p-4 border border-stone-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-forest-100 flex items-center justify-center text-forest-700 font-bold text-sm">{(acc.label || acc.login).charAt(0).toUpperCase()}</div>
                    <div>
                      <p className="font-medium text-stone-800 text-sm">{acc.label || acc.login}</p>
                      <p className="text-xs text-stone-500">{acc.login} &middot; {acc.imap_host}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Circle className={`w-2.5 h-2.5 ${connectionStatus[acc.id] ? "fill-green-500 text-green-500" : "fill-stone-300 text-stone-300"}`} />
                    <button onClick={() => handleDeleteAccount(acc.id)} className="p-1.5 text-stone-400 hover:text-red-600 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => setShowAddAccount(true)} className="flex items-center gap-2 text-forest-600 text-sm font-medium hover:text-forest-700 px-3 py-2"><Plus className="w-4 h-4" /> Add Another</button>
            </div>
          </div>
        )}
      </div>

      {/* Auth Error Modal */}
      {authError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="auth-error-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-7 h-7 text-red-500" /></div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Authentication Error</h3>
            <p className="text-sm text-stone-500 mb-6">Failed to connect to <strong>{authError}</strong>. Verify credentials in Settings.</p>
            <button onClick={() => setAuthError(null)} className="px-5 py-2 rounded-lg text-sm font-medium bg-forest-600 text-white hover:bg-forest-700">OK</button>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      {attachPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="attach-preview-modal" onClick={() => { URL.revokeObjectURL(attachPreview.url); setAttachPreview(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
              <span className="font-medium text-stone-800 text-sm truncate">{attachPreview.filename}</span>
              <div className="flex gap-2">
                <a href={attachPreview.url} download={attachPreview.filename} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100"><Download className="w-3.5 h-3.5" /> Download</a>
                <button onClick={() => { URL.revokeObjectURL(attachPreview.url); setAttachPreview(null); }} className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto flex items-center justify-center bg-stone-100">
              {attachPreview.type.startsWith("image/") ? (
                <img src={attachPreview.url} alt={attachPreview.filename} className="max-w-full max-h-[65vh] object-contain rounded-lg shadow" />
              ) : (
                <iframe src={attachPreview.url} title={attachPreview.filename} className="w-full h-[65vh] rounded-lg border border-stone-200" />
              )}
            </div>
          </div>
        </div>
      )}

      {renderModal()}
    </div>
  );
}
